import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  FormControl,
  InputLabel,
  Tabs,
  Tab,
  Alert,
  Snackbar,
  useMediaQuery,
  useTheme as useMuiTheme,
  Stack,
  Divider,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Restore as RestoreIcon,
  DragHandle as DragIcon,
} from '@mui/icons-material';
import api from '../api/api';
import { useTheme } from '../context/ThemeContext';
import AppIconButton from '../components/ui/AppIconButton';
import { CURRENCY } from '../config/currency';

interface PortionOption {
  name: string;
  priceMultiplier: number;
  displayOrder: number;
  isDefault: boolean;
}

interface PortionTemplate {
  id: number;
  name: string;
  templateType: string;
  options: PortionOption[];
  version: number;
  displayOrder: number;
  deletedAt?: string;
  createdAt: string;
  updatedAt: string;
}


const AdminPortionTemplates: React.FC = () => {
  const muiTheme = useMuiTheme();
  const theme = useTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('md')); // Changed from 'sm' to 'md' for better mobile/tablet detection
  
  const [templates, setTemplates] = useState<PortionTemplate[]>([]);
  const [deletedTemplates, setDeletedTemplates] = useState<PortionTemplate[]>([]);
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<PortionTemplate | null>(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    templateType: '',
    options: [
      { name: 'Small', priceMultiplier: 0, displayOrder: 1, isDefault: true },
      { name: 'Medium', priceMultiplier: 50, displayOrder: 2, isDefault: false },
      { name: 'Large', priceMultiplier: 100, displayOrder: 3, isDefault: false },
    ],
  });

  useEffect(() => {
    fetchTemplates();
    fetchDeletedTemplates();
  }, []);

  const fetchTemplates = async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/admin/portion-templates');
      setTemplates(response.data);
    } catch (error) {
      console.error('Error fetching templates:', error);
      showSnackbar('Failed to load templates', 'error');
    } finally {
      setLoading(false);
    }
  };

  const fetchDeletedTemplates = async () => {
    try {
      const response = await api.get('/api/admin/portion-templates/deleted');
      setDeletedTemplates(response.data);
    } catch (error) {
      console.error('Error fetching deleted templates:', error);
    }
  };

  const handleCreate = () => {
    setEditingTemplate(null);
    setFormData({
      name: '',
      templateType: '',
      options: [
        { name: 'Small', priceMultiplier: 0, displayOrder: 1, isDefault: true },
        { name: 'Medium', priceMultiplier: 50, displayOrder: 2, isDefault: false },
        { name: 'Large', priceMultiplier: 100, displayOrder: 3, isDefault: false },
      ],
    });
    setOpenDialog(true);
  };

  const handleEdit = (template: PortionTemplate) => {
    setEditingTemplate(template);
    setFormData({
      name: template.name,
      templateType: template.templateType,
      options: template.options,
    });
    setOpenDialog(true);
  };

  const handleSave = async () => {
    try {
      setLoading(true);
      if (editingTemplate) {
        await api.put(`/api/admin/portion-templates/${editingTemplate.id}`, formData);
        showSnackbar('Template updated successfully', 'success');
      } else {
        await api.post('/api/admin/portion-templates', formData);
        showSnackbar('Template created successfully', 'success');
      }
      setOpenDialog(false);
      fetchTemplates();
    } catch (error) {
      console.error('Error saving template:', error);
      showSnackbar('Failed to save template', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (!confirm('Are you sure you want to delete this template? It can be restored later.')) {
      return;
    }

    try {
      setLoading(true);
      await api.delete(`/api/admin/portion-templates/${id}`);
      showSnackbar('Template deleted successfully', 'success');
      fetchTemplates();
      fetchDeletedTemplates();
    } catch (error) {
      console.error('Error deleting template:', error);
      showSnackbar('Failed to delete template', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleRestore = async (id: number) => {
    try {
      setLoading(true);
      await api.post(`/api/admin/portion-templates/${id}/restore`);
      showSnackbar('Template restored successfully', 'success');
      fetchTemplates();
      fetchDeletedTemplates();
    } catch (error) {
      console.error('Error restoring template:', error);
      showSnackbar('Failed to restore template', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleAddOption = () => {
    setFormData({
      ...formData,
      options: [
        ...formData.options,
        {
          name: '',
          priceMultiplier: 0,
          displayOrder: formData.options.length + 1,
          isDefault: false,
        },
      ],
    });
  };

  const handleRemoveOption = (index: number) => {
    const newOptions = formData.options.filter((_, i) => i !== index);
    setFormData({ ...formData, options: newOptions });
  };

  const handleOptionChange = (index: number, field: keyof PortionOption, value: any) => {
    const newOptions = [...formData.options];
    newOptions[index] = { ...newOptions[index], [field]: value };

    // If setting as default, unset others
    if (field === 'isDefault' && value === true) {
      newOptions.forEach((opt, i) => {
        if (i !== index) opt.isDefault = false;
      });
    }

    setFormData({ ...formData, options: newOptions });
  };

  const showSnackbar = (message: string, severity: 'success' | 'error') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, pb: { xs: 10, sm: 3 } }}>
      <Box sx={{ 
        display: 'flex', 
        flexDirection: { xs: 'column', sm: 'row' },
        justifyContent: 'space-between', 
        alignItems: { xs: 'stretch', sm: 'center' }, 
        mb: 3,
        gap: 2
      }}>
        <Typography variant={isMobile ? "h5" : "h4"} component="h1">
          Portion Templates Management
        </Typography>
        <Button
          startIcon={<AddIcon />}
          variant="contained"
          size={isMobile ? 'small' : 'medium'}
          onClick={handleCreate}
          fullWidth={isMobile}
          sx={{
            borderRadius: '8px !important',
            textTransform: 'none !important',
            fontWeight: '600 !important',
            px: 3,
            py: 1.5,
            backgroundColor: 'rgba(96, 165, 250, 0.2) !important',
            color: 'var(--text-link) !important',
            borderWidth: '1px !important',
            borderStyle: 'solid !important',
            borderColor: 'rgba(96, 165, 250, 0.31) !important',
            boxShadow: 'none !important',
            '&:hover': {
              backgroundColor: 'rgba(96, 165, 250, 0.25) !important',
              borderColor: 'rgba(96, 165, 250, 0.4) !important',
              boxShadow: 'none !important',
            }
          }}
        >
          Create Template
        </Button>
      </Box>

      <Tabs 
        value={activeTab} 
        onChange={(_, v) => setActiveTab(v)} 
        sx={{ 
          mb: 3,
          '& .MuiTabs-indicator': {
            display: 'none !important',
            height: '0 !important'
          },
        }}
        variant={isMobile ? "fullWidth" : "standard"}
      >
        <Tab 
          label={isMobile ? `Active (${templates.length})` : `Active Templates (${templates.length})`}
          sx={{
            borderRadius: '8px !important',
            textTransform: 'none !important',
            fontWeight: '600 !important',
            fontSize: '1rem !important',
            px: '24px !important',
            minHeight: '40px !important',
            color: 'var(--text-secondary) !important',
            backgroundColor: 'var(--bg-surface) !important',
            border: '1.5px solid var(--border-subtle) !important',
            boxShadow: 'none !important',
            '&.Mui-selected': {
              backgroundColor: 'rgba(96, 165, 250, 0.2) !important',
              borderColor: 'rgba(96, 165, 250, 0.31) !important',
              color: 'var(--text-link) !important',
              boxShadow: 'none !important',
            },
            '&:hover': {
              backgroundColor: 'var(--bg-sunken) !important',
            },
          }}
        />
        <Tab 
          label={isMobile ? `Deleted (${deletedTemplates.length})` : `Deleted Templates (${deletedTemplates.length})`}
          sx={{
            borderRadius: '8px !important',
            textTransform: 'none !important',
            fontWeight: '600 !important',
            fontSize: '1rem !important',
            px: '24px !important',
            minHeight: '40px !important',
            color: 'var(--text-secondary) !important',
            backgroundColor: 'var(--bg-surface) !important',
            border: '1.5px solid var(--border-subtle) !important',
            boxShadow: 'none !important',
            '&.Mui-selected': {
              backgroundColor: 'rgba(96, 165, 250, 0.2) !important',
              borderColor: 'rgba(96, 165, 250, 0.31) !important',
              color: 'var(--text-link) !important',
              boxShadow: 'none !important',
            },
            '&:hover': {
              backgroundColor: 'var(--bg-sunken) !important',
            },
          }}
        />
      </Tabs>

      {activeTab === 0 && (
        <>
          {isMobile ? (
            // Mobile Card Layout
            <Stack spacing={2}>
              {templates.map((template) => (
                <Card key={template.id} variant="outlined">
                  <CardContent sx={{ p: 2 }}>
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="h6" fontWeight="medium" gutterBottom>
                        {template.name}
                      </Typography>
                      <Chip
                        label={template.templateType.replace('_', ' ')}
                        size="small"
                        color="primary"
                        variant="outlined"
                      />
                    </Box>

                    <Divider sx={{ my: 1.5 }} />

                    <Box sx={{ mb: 1.5 }}>
                      <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                        Options
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
                        {template.options.map((opt, idx) => (
                          <Chip
                            key={idx}
                            label={`${opt.name}${opt.priceMultiplier > 0 ? ` +${CURRENCY.symbol}${opt.priceMultiplier}` : ''}`}
                            size="small"
                            color={opt.isDefault ? 'success' : 'default'}
                          />
                        ))}
                      </Box>
                    </Box>

                    <Box sx={{ mb: 1.5 }}>
                      <Typography variant="caption" color="text.secondary" display="block">
                        Version: v{template.version} • Updated: {new Date(template.updatedAt).toLocaleDateString()}
                      </Typography>
                    </Box>

                    <Stack spacing={1} sx={{ mt: 1 }}>
                      <Button
                        fullWidth
                        size="medium"
                        startIcon={<EditIcon />}
                        onClick={() => handleEdit(template)}
                        variant="outlined"
                        sx={{
                          borderRadius: '8px !important',
                          textTransform: 'none !important',
                          fontWeight: '600 !important',
                          backgroundColor: 'rgba(191, 219, 254, 0.5) !important',
                          borderColor: 'rgba(96, 165, 250, 0.31) !important',
                          color: 'var(--text-link) !important',
                          boxShadow: 'none !important',
                          '&:hover': {
                            backgroundColor: 'rgba(191, 219, 254, 0.7) !important',
                            borderColor: 'rgba(96, 165, 250, 0.4) !important',
                            boxShadow: 'none !important'
                          }
                        }}
                      >
                        Edit
                      </Button>
                      <Button
                        fullWidth
                        size="medium"
                        startIcon={<DeleteIcon />}
                        onClick={() => handleDelete(template.id)}
                        variant="outlined"
                        sx={{
                          borderRadius: '8px !important',
                          textTransform: 'none !important',
                          fontWeight: '600 !important',
                          backgroundColor: 'var(--status-bad-bg) !important',
                          borderColor: 'var(--status-bad-border) !important',
                          color: 'var(--status-bad) !important',
                          boxShadow: 'none !important',
                          '&:hover': {
                            backgroundColor: '#FECACA !important', /* basil-todo */
                            borderColor: '#F87171 !important', /* basil-todo */
                            boxShadow: 'none !important'
                          }
                        }}
                      >
                        Delete
                      </Button>
                    </Stack>
                  </CardContent>
                </Card>
              ))}
              {templates.length === 0 && (
                <Card>
                  <CardContent>
                    <Typography color="text.secondary" align="center" sx={{ py: 3 }}>
                      No active templates. Click "Create Template" to add one.
                    </Typography>
                  </CardContent>
                </Card>
              )}
            </Stack>
          ) : (
            // Desktop Table Layout
            <TableContainer component={Paper} sx={{ boxShadow: 'none', borderRadius: 2 }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700, color: 'var(--text-secondary)' }}>Name</TableCell>
                    <TableCell sx={{ fontWeight: 700, color: 'var(--text-secondary)' }}>Type</TableCell>
                    <TableCell sx={{ fontWeight: 700, color: 'var(--text-secondary)' }}>Options</TableCell>
                    <TableCell sx={{ fontWeight: 700, color: 'var(--text-secondary)' }}>Version</TableCell>
                    <TableCell sx={{ fontWeight: 700, color: 'var(--text-secondary)' }}>Last Updated</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700, color: 'var(--text-secondary)' }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {templates.map((template) => (
                    <TableRow key={template.id}>
                      <TableCell sx={{ py: 2 }}>
                        <Typography variant="body1" fontWeight="medium">
                          {template.name}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ py: 2 }}>
                        <Chip
                          label={template.templateType.replace('_', ' ')}
                          size="small"
                          variant="outlined"
                          sx={{
                            backgroundColor: 'rgba(191, 219, 254, 0.5)',
                            borderColor: 'rgba(96, 165, 250, 0.31)',
                            color: 'var(--text-link)',
                            fontWeight: 600
                          }}
                        />
                      </TableCell>
                      <TableCell sx={{ py: 2 }}>
                        <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
                          {template.options.map((opt, idx) => (
                            <Chip
                              key={idx}
                              label={`${opt.name} ${opt.priceMultiplier > 0 ? `(+${CURRENCY.symbol}${opt.priceMultiplier})` : ''}`}
                              size="small"
                              sx={{
                                backgroundColor: 'rgba(243, 244, 246, 0.9)',
                                color: 'var(--text-secondary)',
                                borderColor: 'var(--border-subtle)'
                              }}
                            />
                          ))}
                        </Box>
                      </TableCell>
                      <TableCell sx={{ py: 2 }}>v{template.version}</TableCell>
                      <TableCell sx={{ py: 2 }}>
                        {new Date(template.updatedAt).toLocaleDateString()}
                      </TableCell>
                      <TableCell align="right" sx={{ py: 2, whiteSpace: 'nowrap', minWidth: 120 }}>
                        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
                          <AppIconButton
                            icon={<EditIcon fontSize="small" />}
                            tooltip="Edit template"
                            variant="primary"
                            size="small"
                            onClick={() => handleEdit(template)}
                            data-testid="edit-template"
                          />
                          <AppIconButton
                            icon={<DeleteIcon fontSize="small" />}
                            tooltip="Delete template"
                            variant="danger"
                            size="small"
                            onClick={() => handleDelete(template.id)}
                            data-testid="delete-template"
                          />
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                  {templates.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={6} align="center">
                        <Typography color="text.secondary" sx={{ py: 3 }}>
                          No active templates. Click "Create Template" to add one.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </>
      )}

      {activeTab === 1 && (
        <>
          {isMobile ? (
            // Mobile Card Layout for Deleted Templates
            <Stack spacing={2}>
              {deletedTemplates.map((template) => (
                <Card key={template.id} variant="outlined">
                  <CardContent>
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="h6" fontWeight="medium" gutterBottom>
                        {template.name}
                      </Typography>
                      <Chip
                        label={template.templateType.replace('_', ' ')}
                        size="small"
                        variant="outlined"
                      />
                    </Box>

                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
                      Deleted: {template.deletedAt && new Date(template.deletedAt).toLocaleDateString()}
                    </Typography>

                    <Button
                      fullWidth
                      startIcon={<RestoreIcon />}
                      onClick={() => handleRestore(template.id)}
                      variant="outlined"
                      color="success"
                    >
                      Restore
                    </Button>
                  </CardContent>
                </Card>
              ))}
              {deletedTemplates.length === 0 && (
                <Card>
                  <CardContent>
                    <Typography color="text.secondary" align="center" sx={{ py: 3 }}>
                      No deleted templates.
                    </Typography>
                  </CardContent>
                </Card>
              )}
            </Stack>
          ) : (
            // Desktop Table Layout for Deleted Templates
            <TableContainer component={Paper} sx={{ boxShadow: 'none', borderRadius: 2 }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700, color: 'var(--text-secondary)' }}>Name</TableCell>
                    <TableCell sx={{ fontWeight: 700, color: 'var(--text-secondary)' }}>Type</TableCell>
                    <TableCell sx={{ fontWeight: 700, color: 'var(--text-secondary)' }}>Deleted At</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700, color: 'var(--text-secondary)' }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {deletedTemplates.map((template) => (
                    <TableRow key={template.id}>
                      <TableCell sx={{ py: 2 }}>{template.name}</TableCell>
                      <TableCell sx={{ py: 2 }}>
                        <Chip
                          label={template.templateType.replace('_', ' ')}
                          size="small"
                          variant="outlined"
                          sx={{
                            backgroundColor: 'rgba(191, 219, 254, 0.5)',
                            borderColor: 'rgba(96, 165, 250, 0.31)',
                            color: 'var(--text-link)',
                            fontWeight: 600
                          }}
                        />
                      </TableCell>
                      <TableCell sx={{ py: 2 }}>
                        {template.deletedAt && new Date(template.deletedAt).toLocaleDateString()}
                      </TableCell>
                      <TableCell align="right" sx={{ py: 2 }}>
                        <Button
                          size="small"
                          startIcon={<RestoreIcon />}
                          onClick={() => handleRestore(template.id)}
                          variant="outlined"
                          color="success"
                          sx={{
                            borderRadius: '8px',
                            textTransform: 'none',
                            backgroundColor: 'var(--status-ok-bg)',
                            borderColor: 'var(--status-ok-border)',
                            color: 'var(--status-ok)',
                            '&:hover': {
                              backgroundColor: '#BBF7D0', /* basil-todo */
                              borderColor: '#4ADE80' /* basil-todo */
                            }
                          }}
                        >
                          Restore
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                  {deletedTemplates.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={4} align="center">
                        <Typography color="text.secondary" sx={{ py: 3 }}>
                          No deleted templates.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </>
      )}

      {/* Create/Edit Dialog */}
      <Dialog 
        open={openDialog} 
        onClose={() => setOpenDialog(false)} 
        maxWidth="md" 
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle>
          {editingTemplate ? 'Edit Template' : 'Create Template'}
        </DialogTitle>
        <DialogContent sx={{ px: { xs: 2, sm: 3 }, maxHeight: '80vh', overflowY: 'auto' }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            <TextField
              label="Template Name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              fullWidth
              required
              placeholder="e.g., Small/Medium/Large"
            />

            <TextField
              label="Template Type"
              value={formData.templateType}
              onChange={(e) => setFormData({ ...formData, templateType: e.target.value })}
              fullWidth
              required
              placeholder="e.g., Size Variant, Spice Level, Topping Options"
              helperText="Enter a descriptive category for this template"
            />

            <Typography variant="h6" sx={{ mt: 2 }}>
              Portion Options
            </Typography>

            {formData.options.map((option, index) => (
              <Card key={index} variant="outlined">
                <CardContent>
                  {isMobile ? (
                    // Mobile: Stack all fields vertically
                    <Stack spacing={2}>
                      <TextField
                        label="Option Name"
                        value={option.name}
                        onChange={(e) => handleOptionChange(index, 'name', e.target.value)}
                        fullWidth
                        placeholder="e.g., Small"
                      />
                      <TextField
                        label="Price Modifier (₹)"
                        type="number"
                        value={option.priceMultiplier}
                        onChange={(e) => handleOptionChange(index, 'priceMultiplier', Number(e.target.value))}
                        fullWidth
                      />
                      <FormControl fullWidth>
                        <InputLabel>Default</InputLabel>
                        <Select
                          value={option.isDefault}
                          onChange={(e) => handleOptionChange(index, 'isDefault', e.target.value === 'true')}
                          label="Default"
                        >
                          <MenuItem value="true">Yes</MenuItem>
                          <MenuItem value="false">No</MenuItem>
                        </Select>
                      </FormControl>
                      <Button
                        variant="outlined"
                        onClick={() => handleRemoveOption(index)}
                        disabled={formData.options.length <= 1}
                        startIcon={<DeleteIcon />}
                        fullWidth
                        sx={{
                          borderRadius: '10px !important',
                          textTransform: 'none !important',
                          fontWeight: '600 !important',
                          backgroundColor: 'var(--status-bad-bg) !important',
                          borderColor: 'var(--status-bad-border) !important',
                          borderWidth: '1px !important',
                          color: 'var(--status-bad) !important',
                          boxShadow: 'none !important',
                          '&:hover': {
                            backgroundColor: '#FECACA !important', /* basil-todo */
                            borderColor: '#F87171 !important', /* basil-todo */
                            boxShadow: 'none !important',
                          },
                          '&:disabled': {
                            backgroundColor: 'var(--bg-sunken) !important',
                            borderColor: 'var(--border-default) !important',
                            color: 'var(--text-tertiary) !important',
                          },
                        }}
                      >
                        Remove Option
                      </Button>
                    </Stack>
                  ) : (
                    // Desktop: Horizontal layout
                    <Grid container spacing={1.5} alignItems="center">
                      <Grid item>
                        <AppIconButton
                          icon={<DragIcon fontSize="small" />}
                          tooltip="Drag to reorder"
                          variant="primary"
                          size="small"
                          disabled
                          disableRipple
                          disableFocusRipple
                          sx={{
                            width: '36px',
                            height: '36px',
                            opacity: 1,
                            pointerEvents: 'none',
                            border: `1px solid ${theme.button?.primary?.border || 'rgba(96, 165, 250, 0.31)'}`,
                          }}
                        />
                      </Grid>
                      <Grid item xs>
                        <TextField
                          label="Option Name"
                          value={option.name}
                          onChange={(e) => handleOptionChange(index, 'name', e.target.value)}
                          size="small"
                          fullWidth
                          placeholder="e.g., Small"
                        />
                      </Grid>
                      <Grid item xs={3}>
                        <TextField
                          label="Price Modifier (₹)"
                          type="number"
                          value={option.priceMultiplier}
                          onChange={(e) => handleOptionChange(index, 'priceMultiplier', Number(e.target.value))}
                          size="small"
                          fullWidth
                        />
                      </Grid>
                      <Grid item>
                        <TextField
                          select
                          label="Default"
                          size="small"
                          value={option.isDefault ? 'true' : 'false'}
                          onChange={(e) => handleOptionChange(index, 'isDefault', e.target.value === 'true')}
                          sx={{ minWidth: 140 }}
                        >
                          <MenuItem value="true">Yes</MenuItem>
                          <MenuItem value="false">No</MenuItem>
                        </TextField>
                      </Grid>
                      <Grid item>
                        <AppIconButton
                          icon={<DeleteIcon fontSize="small" />}
                          tooltip="Remove option"
                          variant="danger"
                          size="small"
                          onClick={() => handleRemoveOption(index)}
                          disabled={formData.options.length <= 1}
                          data-testid="remove-option"
                        />
                      </Grid>
                    </Grid>
                  )}
                </CardContent>
              </Card>
            ))}

            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={handleAddOption}
              fullWidth={isMobile}
              sx={{ 
                alignSelf: isMobile ? 'stretch' : 'flex-start',
                borderRadius: '10px !important',
                textTransform: 'none !important',
                fontWeight: '600 !important',
                backgroundColor: 'rgba(191, 219, 254, 0.5) !important',
                borderColor: 'rgba(96, 165, 250, 0.4) !important',
                borderWidth: '1px !important',
                color: 'var(--text-link) !important',
                boxShadow: 'none !important',
                '&:hover': {
                  backgroundColor: 'rgba(191, 219, 254, 0.7) !important',
                  borderColor: 'rgba(96, 165, 250, 0.5) !important',
                  boxShadow: 'none !important',
                },
              }}
            >
              Add Option
            </Button>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button 
            onClick={() => setOpenDialog(false)} 
            fullWidth={isMobile}
            variant="outlined"
            sx={{
              borderRadius: '8px !important',
              textTransform: 'none !important',
              fontWeight: '600 !important',
              fontSize: '1rem !important',
              backgroundColor: 'var(--bg-surface) !important',
              borderColor: 'var(--border-subtle) !important',
              borderWidth: '1px !important',
              color: 'var(--text-secondary) !important',
              boxShadow: 'none !important',
              px: 3,
              py: 1.5,
              '&:hover': {
                backgroundColor: 'var(--bg-sunken) !important',
                borderColor: 'var(--border-strong) !important',
                boxShadow: 'none !important',
              },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            variant="outlined"
            disabled={loading || !formData.name || formData.options.length === 0}
            fullWidth={isMobile}
            sx={{
              borderRadius: '8px !important',
              textTransform: 'none !important',
              fontWeight: '600 !important',
              fontSize: '1rem !important',
              backgroundColor: 'rgba(96, 165, 250, 0.2) !important',
              borderColor: 'rgba(96, 165, 250, 0.31) !important',
              borderWidth: '1px !important',
              color: 'var(--text-link) !important',
              boxShadow: 'none !important',
              px: 3,
              py: 1.5,
              '&:hover': {
                backgroundColor: 'rgba(96, 165, 250, 0.25) !important',
                borderColor: 'rgba(96, 165, 250, 0.4) !important',
                boxShadow: 'none !important',
              },
              '&:disabled': {
                backgroundColor: 'var(--bg-sunken) !important',
                borderColor: 'var(--border-default) !important',
                color: 'var(--text-tertiary) !important',
              },
            }}
          >
            {editingTemplate ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AdminPortionTemplates;
